import { useMutation } from '@tanstack/react-query';
import { FieldValues } from 'react-hook-form';
import { ETypeOfCompetition } from '../../../components/pages/Registration/DefaultVacancy';
import { configApi } from '../../configApi';
import { IDataToRegistration } from '../CandidateArea/useGetDataToRegistration';
import { ISelectiveProcessVancancy } from '../SelectiveProcess/useRequestSelectiveProcessVacancy';
import { IGetRegistration } from './useGetRegistration';

interface ISpecialConditionPayload {
  id: string;
  observations?: string;
}

interface IProtocolPayload {
  id: string;
  social_name?: string;
}

interface ICompetitionSelection {
  id: string;
  name: string;
}

interface IMutationUpdateRegistration {
  data: FieldValues;
  listTypeCompetition?: ISelectiveProcessVancancy[];
  selectiveProcess?: IDataToRegistration;
  registrationId: string;
  exemptionRegistrationId?: string;
  oldData?: IGetRegistration | null;
}

const hasFiles = (files: unknown): files is ArrayLike<File> =>
  Boolean(files && typeof files === 'object' && 'length' in files && Number(files.length) > 0);

const appendFiles = (formData: FormData, field: string, files: unknown) => {
  if (!hasFiles(files)) return;
  for (let index = 0; index < files.length; index++) formData.append(field, files[index]);
};

const normalizeText = (value: unknown) => (value == null ? '' : String(value));
const optionalText = (value: unknown) => normalizeText(value).trim() || undefined;

const normalizeIds = (values: unknown[] = []) =>
  values
    .map((value) => {
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object' && 'id' in value) return String(value.id);
      return '';
    })
    .filter(Boolean)
    .sort();

const equals = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

const parseModality = (value: unknown): ICompetitionSelection | null => {
  if (!value || value === false) return null;
  if (typeof value === 'object' && 'id' in value) {
    return { id: String(value.id), name: 'name' in value ? String(value.name) : '' };
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.id ? { id: String(parsed.id), name: String(parsed.name ?? '') } : null;
  } catch {
    return { id: value, name: '' };
  }
};

const selectedCompetitionIds = (data: FieldValues, vacancies: ISelectiveProcessVancancy[]) => {
  const selectedVacancy = vacancies.find((item) => item.id === data.vacancy);
  const selectedModalities: unknown[] = Array.isArray(data.modality_type) ? data.modality_type : [];
  const modalities: ICompetitionSelection[] = selectedModalities
    .map(parseModality)
    .filter((item): item is ICompetitionSelection => item !== null)
    .map((item) => ({
      ...item,
      name: item.name || selectedVacancy?.type_of_competition.find((type) => type.id === item.id)?.name || '',
    }));

  if (data.type_of_competition === ETypeOfCompetition.SPECIAL_VACANCY) {
    return { ids: normalizeIds(modalities.filter((item) => !/ampla/i.test(item.name))), modalities };
  }

  const requestedType = normalizeText(data.type_of_competition).toLowerCase().replace(/[-_]/g, ' ');
  const explicitId = data.type_of_competition_id;
  const competition = explicitId
    ? { id: String(explicitId) }
    : data.type_of_competition === ETypeOfCompetition.FULL_VACANCY
      ? selectedVacancy?.type_of_competition.find((item) => /ampla/i.test(item.name))
      : data.type_of_competition === ETypeOfCompetition.UFC_SERVER_VACANCY
        ? selectedVacancy?.type_of_competition.find((item) => /ufc/i.test(item.name))
        : selectedVacancy?.type_of_competition.find(
            (item) => item.id === data.type_of_competition || item.name.toLowerCase() === requestedType,
          );

  return { ids: normalizeIds(competition?.id ? [competition.id] : []), modalities };
};

const UpdateRegistration = async ({
  registrationId,
  data,
  selectiveProcess,
  listTypeCompetition = [],
  exemptionRegistrationId,
  oldData,
}: IMutationUpdateRegistration) => {
  const payload = new FormData();

  if (selectiveProcess?.allows_special_condition && data.has_special_condition !== undefined) {
    const enabled = data.has_special_condition === 'yes';
    const oldEnabled = Boolean(oldData?.is_special_condition || oldData?.special_conditions?.length);
    const conditions: ISpecialConditionPayload[] = [];

    (data.selectedSpecialCondition ?? []).forEach((value: string | boolean, index: number) => {
      if (!value) return;
      conditions.push({ id: String(value), observations: optionalText(data.observation?.[index]) });
      appendFiles(payload, `attachment_${value}`, data.attachmentFile?.[index]);
    });

    const previous = (oldData?.special_conditions ?? [])
      .map((item) => ({ id: item.id, observations: optionalText(item.observations) }))
      .sort((a, b) => a.id.localeCompare(b.id));
    const next = [...conditions].sort((a, b) => a.id.localeCompare(b.id));

    if (enabled !== oldEnabled) payload.append('is_special_condition', String(enabled));
    if (enabled && !equals(next, previous)) payload.append('special_conditions_ids', JSON.stringify(next));
  }

  if (data.has_protocol !== undefined) {
    const protocols: IProtocolPayload[] = [];
    (data.selectedProtocol ?? []).forEach((value: string | boolean, index: number) => {
      if (!value) return;
      protocols.push({ id: String(value), social_name: optionalText(data.social_name_protocol?.[index]) });
      appendFiles(payload, `protocol_${value}`, data.protocol_attachmentFile?.[index]);
      appendFiles(payload, `photo_${value}`, data.protocol_photo?.[index]);
    });

    const enabled = protocols.length > 0;
    const oldEnabled = Boolean(oldData?.protocol?.length);
    const previous = (oldData?.protocol ?? [])
      .map((item) => ({ id: item.protocol_type.id, social_name: optionalText(item.social_name) }))
      .sort((a, b) => a.id.localeCompare(b.id));
    const next = [...protocols].sort((a, b) => a.id.localeCompare(b.id));

    if (enabled !== oldEnabled) payload.append('is_protocol', String(enabled));
    if (enabled && !equals(next, previous)) payload.append('protocols_ids', JSON.stringify(next));
  }

  if (data.has_second_vacancy !== undefined) {
    const nextSecondVacancy = data.has_second_vacancy ? normalizeText(data.initial_semester_secondary) : '';
    const oldSecondVacancy = oldData?.second_vacancy?.id ?? '';
    if (nextSecondVacancy !== oldSecondVacancy) payload.append('second_vacancy', nextSecondVacancy);
  }

  if (data.vacancy !== undefined && data.vacancy !== oldData?.selective_process_vacancy?.id) {
    payload.append('selective_process_vacancy', data.vacancy);
  }

  if (data.type_of_competition !== undefined) {
    const { ids: competitionIds, modalities } = selectedCompetitionIds(data, listTypeCompetition);
    const oldCompetitionIds = normalizeIds(oldData?.type_of_competition ?? []);
    const comparableOldCompetitionIds =
      data.type_of_competition === ETypeOfCompetition.SPECIAL_VACANCY
        ? normalizeIds((oldData?.type_of_competition ?? []).filter((item) => !/ampla/i.test(item.name)))
        : oldCompetitionIds;
    if (!equals(competitionIds, comparableOldCompetitionIds)) {
      payload.append('type_of_competition', JSON.stringify(competitionIds));
    }

    const hasPcd = modalities.some((item) => /pcd/i.test(item.name));
    const hasPppiq = modalities.some((item) => /pppiq/i.test(item.name));
    if (hasPcd) appendFiles(payload, 'medical_report_files', data.medical_report);

    const oldPcd = oldData?.reserved_vacancy?.find((item) => /pcd/i.test(item.type));
    if (
      hasPcd &&
      data.cid &&
      data.deficiency &&
      (normalizeText(data.cid) !== normalizeText(oldPcd?.cid) ||
        normalizeText(data.deficiency) !== normalizeText(oldPcd?.deficiency))
    ) {
      payload.append(
        'reserved_vacancy_id',
        JSON.stringify({ ...(oldPcd?.id ? { id: oldPcd.id } : {}), cid: data.cid, deficiency: data.deficiency }),
      );
    }

    const nextSubtypes = normalizeIds(
      hasPppiq ? (data.sub_type_of_competition ?? []).filter((item: unknown) => Boolean(item)) : [],
    );
    const oldSubtypes = normalizeIds(oldData?.sub_types_of_competitions ?? []);
    if (!equals(nextSubtypes, oldSubtypes)) {
      payload.append('sub_types_of_competitions', JSON.stringify(nextSubtypes));
    }
  }

  if (
    selectiveProcess?.firearms_act &&
    data.weapon_carry_permit !== undefined &&
    data.weapon_carry_permit !== oldData?.has_gun_license
  ) {
    payload.append('has_gun_license', String(data.weapon_carry_permit));
  }

  if (selectiveProcess?.juror_under_article_440 && data.juror_art_440_cpp !== undefined) {
    const nextJuror = data.juror_art_440_cpp === 'yes';
    if (nextJuror !== oldData?.juror_art_440_cpp) payload.append('juror_art_440_cpp', String(nextJuror));
  }

  if (data.terms !== undefined && Boolean(data.terms) !== oldData?.according_to_the_terms) {
    payload.append('according_to_the_terms', String(Boolean(data.terms)));
  }

  if (
    data.eligibility_declaration !== undefined &&
    Boolean(data.eligibility_declaration) !== oldData?.eligibility_declaration
  ) {
    payload.append('eligibility_declaration', String(Boolean(data.eligibility_declaration)));
  }

  const targetRegistrationId = exemptionRegistrationId || registrationId;
  const response = await configApi.patch(`registration/update/${targetRegistrationId}/`, payload);
  return response.data;
};

export const useUpdateRegistration = () =>
  useMutation({
    mutationKey: ['update_registration'],
    mutationFn: (payload: IMutationUpdateRegistration) => UpdateRegistration(payload),
  });
